// App Version Management System
// Handles version tracking, migrations, and backwards compatibility

export const APP_VERSION = '2.0.0';
export const APP_BUILD_DATE = '2024-12-12';

// Version history for migrations
const VERSION_HISTORY = [
  { version: '1.0.0', date: '2024-01-01', description: 'Initial release' },
  { version: '1.5.0', date: '2024-06-01', description: 'Added calculators' },
  { version: '2.0.0', date: '2024-12-12', description: 'Major update with AI features, advanced calculators, and PWA support' }
];

// Data schema versions
const DATA_SCHEMAS = {
  materials: 2,
  projects: 2,
  settings: 2,
  customItems: 2
};

// Migration functions for each data type
const migrations = {
  materials: {
    1: (data) => {
      // v1 to v2: Add category field if missing
      return data.map(item => ({
        ...item,
        category: item.category || 'Custom',
        createdAt: item.createdAt || new Date().toISOString()
      }));
    }
  },
  projects: {
    1: (data) => {
      // v1 to v2: Add metadata
      return {
        ...data,
        version: 2,
        updatedAt: new Date().toISOString(),
        aiAnalysis: data.aiAnalysis || null
      };
    }
  },
  settings: {
    1: (data) => {
      // v1 to v2: Add new settings
      return {
        ...data,
        version: 2,
        aiEnabled: true,
        autoSave: true,
        theme: data.theme || 'system'
      };
    }
  }
};

// Check if data needs migration
export const needsMigration = (dataType, currentVersion) => {
  const latestVersion = DATA_SCHEMAS[dataType];
  return currentVersion < latestVersion;
};

// Run migrations on data
export const migrateData = (dataType, data, fromVersion) => {
  let currentVersion = fromVersion;
  let migratedData = data;
  
  const typeMigrations = migrations[dataType];
  if (!typeMigrations) return data;
  
  const latestVersion = DATA_SCHEMAS[dataType];
  
  while (currentVersion < latestVersion) {
    const migrationFn = typeMigrations[currentVersion];
    if (migrationFn) {
      try {
        migratedData = migrationFn(migratedData);
        console.log(`Migrated ${dataType} from v${currentVersion} to v${currentVersion + 1}`);
      } catch (error) {
        console.error(`Migration failed for ${dataType} v${currentVersion}:`, error);
        return data; // Return original data on failure
      }
    }
    currentVersion++;
  }
  
  return migratedData;
};

// Initialize app data with version checking
export const initializeAppData = () => {
  const storedVersion = localStorage.getItem('app_version');
  const storedDataVersion = localStorage.getItem('data_schema_version');
  
  // Check if this is a new installation
  if (!storedVersion) {
    localStorage.setItem('app_version', APP_VERSION);
    localStorage.setItem('data_schema_version', JSON.stringify(DATA_SCHEMAS));
    localStorage.setItem('app_install_date', new Date().toISOString());
    return { isNewInstall: true, previousVersion: null };
  }
  
  // Check if app was updated
  const wasUpdated = storedVersion !== APP_VERSION;
  
  if (wasUpdated) {
    // Store previous version for reference
    localStorage.setItem('previous_app_version', storedVersion);
    localStorage.setItem('app_version', APP_VERSION);
    localStorage.setItem('app_last_update', new Date().toISOString());
    
    // Run data migrations if needed
    if (storedDataVersion) {
      try {
        const oldSchemas = JSON.parse(storedDataVersion);
        
        // Check each data type for needed migrations
        for (const [dataType, latestVersion] of Object.entries(DATA_SCHEMAS)) {
          const oldVersion = oldSchemas[dataType] || 1;
          if (oldVersion < latestVersion) {
            // Get stored data
            const storageKey = `mm_${dataType}`;
            const storedData = localStorage.getItem(storageKey);
            
            if (storedData) {
              const parsedData = JSON.parse(storedData);
              const migratedData = migrateData(dataType, parsedData, oldVersion);
              localStorage.setItem(storageKey, JSON.stringify(migratedData));
            }
          }
        }
      } catch (error) {
        console.error('Data migration error:', error);
      }
    }
    
    localStorage.setItem('data_schema_version', JSON.stringify(DATA_SCHEMAS));
  }
  
  return { 
    isNewInstall: false, 
    previousVersion: storedVersion,
    wasUpdated 
  };
};

// Get version info
export const getVersionInfo = () => {
  return {
    version: APP_VERSION,
    buildDate: APP_BUILD_DATE,
    history: VERSION_HISTORY,
    dataSchemas: DATA_SCHEMAS,
    installDate: localStorage.getItem('app_install_date'),
    lastUpdate: localStorage.getItem('app_last_update'),
    previousVersion: localStorage.getItem('previous_app_version')
  };
};

// Safe data storage with version tracking
export const saveData = (key, data, dataType = null) => {
  try {
    const wrappedData = {
      data,
      version: dataType ? DATA_SCHEMAS[dataType] : 1,
      savedAt: new Date().toISOString(),
      appVersion: APP_VERSION
    };
    localStorage.setItem(key, JSON.stringify(wrappedData));
    return true;
  } catch (error) {
    console.error('Failed to save data:', error);
    return false;
  }
};

// Safe data loading with migration
export const loadData = (key, dataType = null, defaultValue = null) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return defaultValue;
    
    const parsed = JSON.parse(stored);
    
    // Handle legacy data (not wrapped)
    if (!parsed.version && !parsed.data) {
      // This is legacy data, wrap it
      const legacyData = parsed;
      if (dataType) {
        return migrateData(dataType, legacyData, 1);
      }
      return legacyData;
    }
    
    // Handle wrapped data
    let data = parsed.data || parsed;
    const dataVersion = parsed.version || 1;
    
    // Run migration if needed
    if (dataType && needsMigration(dataType, dataVersion)) {
      data = migrateData(dataType, data, dataVersion);
      // Save migrated data
      saveData(key, data, dataType);
    }
    
    return data;
  } catch (error) {
    console.error('Failed to load data:', error);
    return defaultValue;
  }
};

// Check for app updates (can be extended to check server)
export const checkForUpdates = async () => {
  // In a real app, this would check a server endpoint
  // For now, return current version info
  return {
    currentVersion: APP_VERSION,
    latestVersion: APP_VERSION,
    updateAvailable: false,
    releaseNotes: null
  };
};

// Export user data for backup
export const exportUserData = () => {
  const data = {
    version: APP_VERSION,
    exportDate: new Date().toISOString(),
    materials: loadData('calculatorMaterials'),
    bulkMaterials: loadData('bulkCalculatorMaterials'),
    excavationMaterials: loadData('excavationMaterials'),
    customTrucks: loadData('customTrucks'),
    customPipes: loadData('customPipes'),
    customTanks: loadData('customTanks'),
    projectInfo: loadData('projectInfo'),
    settings: loadData('estimateSettings'),
    laborTypes: loadData('laborTypes'),
    equipmentTypes: loadData('equipmentTypes')
  };
  
  return data;
};

// Import user data from backup
export const importUserData = (importedData) => {
  try {
    // Validate import data
    if (!importedData || !importedData.version) {
      throw new Error('Invalid import data format');
    }
    
    // Import each data type
    const dataTypes = [
      'materials', 'bulkMaterials', 'excavationMaterials',
      'customTrucks', 'customPipes', 'customTanks',
      'projectInfo', 'settings', 'laborTypes', 'equipmentTypes'
    ];
    
    for (const type of dataTypes) {
      if (importedData[type]) {
        const storageKey = type === 'materials' ? 'calculatorMaterials' 
          : type === 'bulkMaterials' ? 'bulkCalculatorMaterials'
          : type;
        localStorage.setItem(storageKey, JSON.stringify(importedData[type]));
      }
    }
    
    return { success: true, message: 'Data imported successfully' };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export default {
  APP_VERSION,
  APP_BUILD_DATE,
  initializeAppData,
  getVersionInfo,
  saveData,
  loadData,
  checkForUpdates,
  exportUserData,
  importUserData
};
